'use client'
import * as React from 'react'
import { Activity } from './activity'
import { useReplicacheQueryV2 } from '~/hooks-data/use-replicache-query'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { teamSpaceMutators } from '@basehub/data/replicache/mutators/team-space/mutators'
import gsap from 'gsap'
import { useSelectedTeam } from '~/hooks-data/use-selected-team'
import {
  PublicTeam,
  isPublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'
import { SidebarSection } from '../components/section'
import { ExpandButton } from '../components/expand-button'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import { Team } from '@basehub/data/replicache/index'

export const RecentActivity = ({
  staticTeamData,
}: {
  staticTeamData?: PublicTeam | Team
}) => {
  const { data } = useTeamReplicache()
  const { team } = useSelectedTeam(staticTeamData)

  const replicache = data?.replicache
  const eventsQuery = useReplicacheQueryV2({
    key: ['listEvents', team?.id],
    replicache,
    fetcher: teamSpaceMutators.listEvents,
    enabled: !isPublicTeam(team),
  })

  const events = isPublicTeam(team) ? team.publicEvents : eventsQuery.data
  const isLoading = React.useMemo(
    () => !isPublicTeam(team) && eventsQuery.isInitialLoading,
    [eventsQuery.isInitialLoading, team]
  )

  const [pagination, setPagination] = React.useState({
    page: 1,
    perPage: 3,
  })

  // do we want to fetch and sort all events here?
  const paginatedData = React.useMemo(() => {
    if (!events) return []
    return events
      .sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })
      .slice(0, pagination.page * pagination.perPage)
  }, [events, pagination])

  const handleNextPage = () => {
    if (events && events.length <= pagination.page * pagination.perPage) return
    setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
  }
  const isFirstRender = React.useRef(true)
  const eventsContainerRef = React.useRef<HTMLUListElement>(null)

  React.useEffect(() => {
    if (
      !eventsContainerRef.current ||
      !eventsContainerRef.current.childNodes.length
    ) {
      return
    }
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    const tl = gsap
      .timeline({ defaults: { duration: 0.63, ease: 'power3.out' } })
      .fromTo(
        eventsContainerRef.current.childNodes,
        { yPercent: -100 },
        { yPercent: 0 }
      )

    return () => {
      tl.kill()
    }
  }, [team?.id])

  return (
    <SidebarSection title="Recent activity" className="gap-1.5">
      <div className="relative flex flex-col flex-1 overflow-hidden">
        {(() => {
          if (events === null || (events && events.length < 1)) {
            return (
              <p className="text-xs leading-4 text-dark-faint">
                Team events will be listed here
              </p>
            )
          }

          return (
            <>
              <ul ref={eventsContainerRef} className="space-y-2">
                {paginatedData.map((activity) => (
                  <Activity key={activity.id} activity={activity} />
                ))}
              </ul>
              {!isLoading &&
                events &&
                events.length > pagination.page * pagination.perPage && (
                  <ExpandButton
                    icon={<ChevronDownIcon />}
                    onClick={handleNextPage}
                    className="mt-2"
                  >
                    Load more
                  </ExpandButton>
                )}
            </>
          )
        })()}
      </div>
    </SidebarSection>
  )
}
